/**
 * LumaJSON Viewer Panel (Read-Only)
 *
 * Read-only visualization of the final LumaJSON export format.
 * Displays the workflow as an interactive React Flow diagram.
 * This is the final format before export to Luma - NOT EDITABLE.
 * For visual editing, use the BPMN Editor.
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactFlow, {
  Controls,
  Background,
  MiniMap,
  BackgroundVariant,
  type NodeTypes,
  type Node,
  type Edge
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useProjectStore, type ProjectArtifact, type ArtifactType } from '@/stores/projectStore';
import { useToastStore } from '@/stores/toastStore';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Sparkles, Network, AlertCircle, Code, Database, Workflow, Zap, Eye } from 'lucide-react';
import { CustomNode } from '../canvas/nodes/CustomNode';
import { LumaSkillTransformers, TransformError } from '@/utils/lumaSkillTransformers';
import type { LumaSkill } from '@/types/lumaSkill';
import { useLumaJsonGeneration } from '@/hooks/useLumaJsonGeneration';

// Register custom node type
const nodeTypes: NodeTypes = {
  custom: CustomNode
};

// Artifact type icons
const ARTIFACT_ICONS: Record<ArtifactType, React.FC<{ className?: string }>> = {
  SKILL: Workflow,
  SUBSKILL: Workflow,
  SUBFLOW: Workflow,
  ATTRIBUTE: Database,
  INTEGRATION: Zap,
  FACET: Code
};

// Artifact type colors
const ARTIFACT_COLORS: Record<ArtifactType, string> = {
  SKILL: 'from-blue-500/10 to-blue-600/5 border-blue-300',
  SUBSKILL: 'from-blue-400/10 to-blue-500/5 border-blue-200',
  SUBFLOW: 'from-cyan-500/10 to-cyan-600/5 border-cyan-300',
  ATTRIBUTE: 'from-green-500/10 to-green-600/5 border-green-300',
  INTEGRATION: 'from-purple-500/10 to-purple-600/5 border-purple-300',
  FACET: 'from-orange-500/10 to-orange-600/5 border-orange-300'
};

interface ArtifactCardProps {
  artifact: ProjectArtifact;
  onGenerateLumaJSON: (artifactId: string) => void;
  isGenerating: boolean;
}

const ArtifactCard: React.FC<ArtifactCardProps> = ({
  artifact,
  onGenerateLumaJSON,
  isGenerating
}) => {
  const Icon = ARTIFACT_ICONS[artifact.type];
  const colorClass = ARTIFACT_COLORS[artifact.type];

  // Get version info from artifact metadata
  const lumascriptVersion = artifact.lumascriptVersion ?? 0;
  const lumajsonVersion = artifact.lumajsonVersion ?? 0;
  const hasLumaJSON = artifact.lumaJson !== null;
  const isDesync = lumascriptVersion !== lumajsonVersion;

  return (
    <div className={`p-4 rounded-lg border bg-gradient-to-br ${colorClass} hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-foreground" />
          <div>
            <h3 className="font-medium text-sm">{artifact.name}</h3>
            <p className="text-xs text-muted-foreground">{artifact.identifier}</p>
          </div>
        </div>
        <span className="text-xs px-2 py-1 rounded bg-background/50 border">{artifact.type}</span>
      </div>

      {artifact.description && (
        <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{artifact.description}</p>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
        <span>v{lumascriptVersion}.{lumajsonVersion}</span>
        <div className="flex items-center gap-2">
          {isDesync && hasLumaJSON && (
            <span className="text-amber-600 font-medium">⚠ Out of Sync</span>
          )}
          {hasLumaJSON ? (
            <span className="text-green-600">✓ Generated</span>
          ) : (
            <span className="text-amber-600">○ Not Generated</span>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onGenerateLumaJSON(artifact.id)}
          disabled={isGenerating}
          className="flex-1"
        >
          <Code className="w-3 h-3 mr-1" />
          {hasLumaJSON ? 'Regenerate' : 'Generate'} LumaJSON
        </Button>
      </div>
    </div>
  );
};

export const LumaJSONViewerPanel: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const loadArtifacts = useProjectStore((state) => state.loadArtifacts);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);
  const setSelectedArtifact = useProjectStore((state) => state.setSelectedArtifact);
  const activeStage = useProjectStore((state) => state.ui.activeStage);
  const { addToast } = useToastStore();
  const { startLumaJsonGeneration } = useLumaJsonGeneration();

  // Get current project and artifact
  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = project && selectedArtifactId ? project.artifacts[selectedArtifactId] : null;

  const [generatingArtifactId, setGeneratingArtifactId] = useState<string | null>(null);
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [pendingArtifactId, setPendingArtifactId] = useState<string | null>(null);
  const [fallbackRequest, setFallbackRequest] = useState<{
    artifactId: string;
    reason?: string;
    message?: string;
  } | null>(null);

  // Read-only state - no change handlers
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [parseError, setParseError] = React.useState<string | null>(null);
  const [hasDimensions, setHasDimensions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isViewerActive = activeStage === 'LUMAJSON_VIEWER';

  const refreshArtifactData = useCallback(async () => {
    if (!activeProjectId) return;
    await loadArtifacts(activeProjectId);
  }, [activeProjectId, loadArtifacts]);

  // ResizeObserver to detect when container gets dimensions
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const hasSize = width > 0 && height > 0;
        setHasDimensions(hasSize);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Parse and transform LumaJSON whenever the selected artifact's JSON changes
  useEffect(() => {
    if (!artifact) {
      setNodes([]);
      setEdges([]);
      setParseError(null);
      return;
    }

    const artifactLumaJson = artifact.lumaJson;

    if (!artifactLumaJson) {
      setNodes([]);
      setEdges([]);
      setParseError(null);
      return;
    }

    try {
      const ootbSkill = typeof artifactLumaJson === 'string'
        ? JSON.parse(artifactLumaJson)
        : artifactLumaJson;

      const skill: LumaSkill = LumaSkillTransformers.fromOOTBFormat(ootbSkill);
      const needsLayout = skill.nodes.some(n => n.position.x === 0 && n.position.y === 0);
      const layoutedSkill = needsLayout
        ? LumaSkillTransformers.autoLayout(skill)
        : skill;

      const { nodes: flowNodes, edges: flowEdges } = LumaSkillTransformers.toReactFlow(layoutedSkill);
      setNodes(flowNodes);
      setEdges(flowEdges);
      setParseError(null);
    } catch (error: any) {
      console.error('[LumaJSONViewer] Failed to parse LumaJSON:', error);
      const message = error instanceof TransformError ? error.message : 'Invalid LumaJSON format';
      setParseError(message);
      addToast('error', message);
      setNodes([]);
      setEdges([]);
    }
  }, [artifact, addToast]);

  // Handle manual auto-layout button (re-layout visually, doesn't change data)
  const handleAutoLayout = useCallback(() => {
    if (!artifact?.lumaJson) return;

    try {
      const ootbSkill = typeof artifact.lumaJson === 'string'
        ? JSON.parse(artifact.lumaJson)
        : artifact.lumaJson;

      const skill: LumaSkill = LumaSkillTransformers.fromOOTBFormat(ootbSkill);
      const layoutedSkill = LumaSkillTransformers.autoLayout(skill, {
        direction: 'TB',
        nodeWidth: 200,
        nodeHeight: 100,
        rankSep: 120,
        nodeSep: 80
      });

      const { nodes: flowNodes, edges: flowEdges } = LumaSkillTransformers.toReactFlow(layoutedSkill);
      setNodes(flowNodes);
      setEdges(flowEdges);
      addToast('success', 'Auto-layout applied');
    } catch (error) {
      addToast('error', 'Failed to apply auto-layout');
    }
  }, [artifact, addToast]);

  // Navigate back to BPMN Editor
  const handleToBPMNEditor = () => {
    setActiveStage('BPMN_EDITOR');
  };

  const ensureStagesReady = (targetArtifact: ProjectArtifact | undefined): boolean => {
    if (!targetArtifact) {
      addToast('error', 'Artifact not found in the current project.');
      return false;
    }

    const lumaScriptStage = targetArtifact.stages?.LUMASCRIPT;
    const bpmnStage = targetArtifact.stages?.BPMN;

    if (!lumaScriptStage?.content) {
      addToast('error', 'Generate and save LumaScript before creating LumaJSON.');
      return false;
    }

    if (!bpmnStage?.content) {
      addToast('error', 'Generate and save BPMN before creating LumaJSON.');
      return false;
    }

    if (lumaScriptStage.isDirty || bpmnStage.isDirty) {
      addToast('warning', 'Save LumaScript and BPMN changes before generating LumaJSON.');
      return false;
    }

    return true;
  };

  const executeLumaJsonGeneration = async (artifactId: string, options: { useFallback?: boolean } = {}) => {
    if (!project) {
      addToast('error', 'Load a project before generating LumaJSON.');
      return;
    }

    const targetArtifact = project.artifacts[artifactId];
    if (!ensureStagesReady(targetArtifact)) {
      return;
    }

    setGeneratingArtifactId(artifactId);

    try {
      const result = await startLumaJsonGeneration({
        artifactId,
        artifactName: targetArtifact.name,
        artifactType: targetArtifact.type,
        projectId: project.id,
        onRefresh: refreshArtifactData,
        useFallback: options.useFallback
      });

      if (!result) {
        return;
      }

      if (result.status === 'fallback-required') {
        setFallbackRequest({
          artifactId,
          reason: result.fallbackReason,
          message: result.fallbackMessage
        });
        return;
      }

      if (result.status === 'completed' && artifact && artifact.id === artifactId) {
        setActiveStage('LUMAJSON');
      }
    } finally {
      setGeneratingArtifactId(null);
    }
  };

  // Handle Generate LumaJSON button
  const handleGenerateLumaJSON = async (artifactId: string) => {
    if (!project) {
      addToast('error', 'Load a project before generating LumaJSON.');
      return;
    }

    const targetArtifact = project.artifacts[artifactId];
    if (!ensureStagesReady(targetArtifact)) {
      return;
    }

    if (targetArtifact.lumaJson) {
      setPendingArtifactId(artifactId);
      setShowOverwriteDialog(true);
      return;
    }

    await executeLumaJsonGeneration(artifactId);
  };

  const handleConfirmOverwrite = async () => {
    if (!pendingArtifactId) {
      return;
    }

    setShowOverwriteDialog(false);
    await executeLumaJsonGeneration(pendingArtifactId);
    setPendingArtifactId(null);
  };

  const handleCancelOverwrite = () => {
    if (pendingArtifactId) {
      setSelectedArtifact(pendingArtifactId);
      setActiveStage('LUMAJSON');
    }

    setPendingArtifactId(null);
    setShowOverwriteDialog(false);
  };

  const handleFallbackConfirm = async () => {
    if (!fallbackRequest) return;
    const { artifactId } = fallbackRequest;
    setFallbackRequest(null);
    await executeLumaJsonGeneration(artifactId, { useFallback: true });
  };

  const handleFallbackCancel = () => {
    setFallbackRequest(null);
  };

  // Get all artifacts in the project
  const allArtifacts = project ? Object.values(project.artifacts) : [];

  // Show empty state if no project
  const pendingArtifact = pendingArtifactId ? project?.artifacts[pendingArtifactId] : null;
  const overwriteDialog = (
    <ConfirmDialog
      isOpen={showOverwriteDialog}
      title="Overwrite existing LumaJSON?"
      message={pendingArtifact
        ? `"${pendingArtifact.name}" already has LumaJSON. Overwrite it with a fresh AI-generated version?`
        : 'This artifact already has LumaJSON. Overwrite it with a fresh AI-generated version?'}
      confirmLabel="Overwrite"
      cancelLabel="View existing"
      onConfirm={handleConfirmOverwrite}
      onCancel={handleCancelOverwrite}
    />
  );

  const fallbackDialog = (
    <ConfirmDialog
      isOpen={Boolean(fallbackRequest)}
      title="Use deterministic fallback?"
      message={fallbackRequest?.message
        ? `${fallbackRequest.message}${fallbackRequest.reason ? `\n\nReason: ${fallbackRequest.reason}.` : ''}\n\nWould you like to generate LumaJSON using the deterministic transformer instead?`
        : 'The LLM output could not be validated. Generate LumaJSON using the deterministic transformer instead?'}
      confirmLabel="Use Fallback"
      cancelLabel="Cancel"
      onConfirm={handleFallbackConfirm}
      onCancel={handleFallbackCancel}
    />
  );

  if (!project) {
    return (
      <>
        {overwriteDialog}
        <div className="w-full h-full flex items-center justify-center bg-gray-50">
          <div className="text-center text-muted-foreground">
            <p className="text-sm">No project selected. Create or open a project to get started.</p>
          </div>
        </div>
      </>
    );
  }

  // When no artifact is selected, show the artifact overview grid
  if (!artifact) {
    return (
      <>
        {overwriteDialog}
        <div className="w-full h-full flex flex-col">
          <div className="h-10 border-b border-border bg-background flex items-center justify-between px-4 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">LumaJSON Viewer</span>
              <span className="text-xs text-muted-foreground">
                ({allArtifacts.length} artifact{allArtifacts.length !== 1 ? 's' : ''})
              </span>
            </div>
            <button
              onClick={handleToBPMNEditor}
              className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors flex items-center gap-1.5"
            >
              To BPMN Editor
            </button>
          </div>

          <div className="flex-1 overflow-auto bg-gray-50 p-6">
            {allArtifacts.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-muted-foreground max-w-md">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-sm mb-2 font-medium">No Artifacts Yet</p>
                  <p className="text-xs text-gray-500 mb-4">
                    Generate artifacts from your prompts to see them here.
                  </p>
                  <Button size="sm" onClick={handleToBPMNEditor}>
                    Go to BPMN Editor
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-7xl mx-auto">
                {allArtifacts.map((art) => (
                  <ArtifactCard
                    key={art.id}
                    artifact={art}
                    onGenerateLumaJSON={handleGenerateLumaJSON}
                    isGenerating={generatingArtifactId === art.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // Get detail artifact (when viewing skill flow)
  const detailArtifact = artifact;
  const detailLumaSkillJson = detailArtifact?.lumaJson || null;

  // Show error state if parse failed (detail view)
  if (parseError) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="h-10 border-b border-border bg-background flex items-center justify-between px-4 flex-shrink-0">
          <span className="text-xs text-muted-foreground">{detailArtifact?.name}</span>
        </div>
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <p className="text-sm font-medium mb-2">Failed to Load LumaJSON</p>
            <p className="text-xs text-muted-foreground mb-4">{parseError}</p>
            <Button size="sm" onClick={handleToBPMNEditor}>
              Go to BPMN Editor
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no LumaJSON generated yet (detail view)
  if (!detailLumaSkillJson) {
    return (
      <div className="w-full h-full flex flex-col">
        {/* Empty State */}
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center text-muted-foreground max-w-md">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-sm mb-2 font-medium">No LumaJSON Generated Yet</p>
            <p className="text-xs text-gray-500 mb-4">
              Generate BPMN and LumaJSON to see the final export format here.
            </p>
            <Button size="sm" onClick={handleToBPMNEditor}>
              Go to BPMN Editor
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render React Flow canvas (detail view) - READ-ONLY
  return (
    <>
      {overwriteDialog}
      {fallbackDialog}
      <div className="w-full h-full flex flex-col">
      {/* Action Bar */}
      <div className="h-10 border-b border-border bg-background flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-2 text-xs">
          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">{detailArtifact?.name}</span>
          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Read-Only</span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={handleAutoLayout}
            className="flex items-center gap-2"
            title="Re-apply automatic layout"
          >
            <Network className="w-4 h-4" />
            Auto-Layout
          </Button>

          <div className="text-xs text-muted-foreground">
            {nodes.length} nodes, {edges.length} connections
          </div>
        </div>
      </div>

      {/* React Flow Canvas - READ-ONLY (no edit handlers) */}
      <div ref={containerRef} className="flex-1 relative bg-gray-50" style={{ minHeight: 0, minWidth: 0 }}>
        {hasDimensions && isViewerActive && (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.2}
            maxZoom={2}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#64748b', strokeWidth: 2 }
            }}
            proOptions={{ hideAttribution: true }}
            style={{ width: '100%', height: '100%' }}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={16}
              size={1}
              color="#cbd5e1"
            />
            <Controls showInteractive={false} />
            <MiniMap
              nodeColor={() => {
                return '#94a3b8'; // Default gray
              }}
              maskColor="rgba(0, 0, 0, 0.05)"
              className="bg-white border border-gray-300 rounded"
            />
          </ReactFlow>
        )}
      </div>
      </div>
    </>
  );
};
